import { useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { Icon } from "@design-system/Icon/Icon";
import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { TextField } from "@design-system/TextField/TextField";
import {
  useAutotaskConnectionQuery,
  useSaveAutotaskConnectionMutation,
  useDisconnectAutotaskConnectionMutation,
} from "@graphql/integrations/integrations.g";

export const AutotaskIntegrationCard = () => {
  const { data, isLoading, refetch } = useAutotaskConnectionQuery();
  const connection = data?.autotaskConnection;
  const isConnected = !!connection && connection.isActive;

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [apiUsername, setApiUsername] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [integrationCode, setIntegrationCode] = useState("DODD3UC52P3TSTDK5SPUX6IURK");
  const [apiBaseUrl, setApiBaseUrl] = useState("https://webservices19.autotask.net");

  const saveMutation = useSaveAutotaskConnectionMutation({
    onSuccess: (resData) => {
      if (resData.saveAutotaskConnection?.errors?.length) {
        toast.error(resData.saveAutotaskConnection.errors[0]?.message || "Failed to save");
        return;
      }
      toast.success("Successfully connected to Autotask!");
      setIsPanelOpen(false);
      refetch();
    },
    onError: () => toast.error("Failed to connect. Please check credentials."),
  });

  const disconnectMutation = useDisconnectAutotaskConnectionMutation({
    onSuccess: () => {
      toast.success("Successfully disconnected from Autotask.");
      refetch();
    },
    onError: () => toast.error("Failed to disconnect."),
  });

  const handleConnectClick = () => {
    setApiUsername(connection?.apiUsername || "");
    setIntegrationCode(connection?.integrationCode || "DODD3UC52P3TSTDK5SPUX6IURK");
    setApiBaseUrl(connection?.apiBaseUrl || "https://webservices19.autotask.net");
    setApiSecret("");
    setIsPanelOpen(true);
  };

  const handleSave = () => {
    saveMutation.mutate({
      input: {
        apiUsername,
        apiSecret,
        integrationCode,
        apiBaseUrl,
      },
    });
  };

  return (
    <>
      <Card>
        <Card.Header withBottomPadding>
          <Card.Header.Title>Autotask</Card.Header.Title>
        </Card.Header>
        <Card.Body grow>
          <Box direction="column" gap={6}>
            <Box direction="column" gap={16}>
              <Box>
                Connect your Autotask account to enable two-way sync for alerts
                and seamless management.
              </Box>
              <Box>
                <a
                  href="https://doc.clickup.com/9015827861/d/h/8cp57cn-495/f04c5feb77a3842"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "var(--color-primary-500)", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  View installation instructions
                  <Icon icon="faArrowUpRightFromSquare" size="sm" />
                </a>
              </Box>
            </Box>
            <Box justify="flex-end" fullStretch>
              {isConnected ? (
                <Button
                  color="primary"
                  variant="outlined"
                  style={{ marginTop: 16 }}
                  onClick={() => disconnectMutation.mutate({})}
                  disabled={disconnectMutation.isPending || isLoading}
                >
                  {disconnectMutation.isPending ? "Disconnecting..." : "Disconnect Autotask"}
                </Button>
              ) : (
                <Button
                  color="primary"
                  style={{ marginTop: 16 }}
                  onClick={handleConnectClick}
                  disabled={isLoading}
                >
                  Connect to Autotask
                </Button>
              )}
            </Box>
          </Box>
        </Card.Body>
      </Card>

      <SlideInPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        onCancel={() => setIsPanelOpen(false)}
        onApply={handleSave}
        applyButtonText="Connect"
        title="Autotask Connection"
      >
        <Box direction="column" gap={20}>
          <FieldGroup label="API Tracking Identifier (Username)">
            <TextField
              value={apiUsername}
              onChange={(e) => setApiUsername(e.target.value)}
              placeholder="e.g. user@msp.com"
            />
          </FieldGroup>
          <FieldGroup label="API Secret">
            <TextField
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder="Enter your API secret"
            />
          </FieldGroup>
          <FieldGroup label="Integration Code">
            <TextField
              value={integrationCode}
              onChange={(e) => setIntegrationCode(e.target.value)}
              placeholder="Enter Tracking/Integration Code"
            />
          </FieldGroup>
          <FieldGroup label="API Base URL">
            <TextField
              value={apiBaseUrl}
              onChange={(e) => setApiBaseUrl(e.target.value)}
              placeholder="https://webservices19.autotask.net"
            />
          </FieldGroup>
        </Box>
      </SlideInPanel>
    </>
  );
};
